// Library methods
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { ViewFileIcon } from "../../../assets/icons";

// utilities
import CustomCheckbox from "../CustomCheckbox";
import { getLocalDate } from "../../../utils/dateHelper";
import { useMemo } from "react";
import { gridLabels } from "../../../enums";
import useClinic from "../../../hooks/useClinic";

const InfoDisplay = ({ label, value, gridSize, noWrap = false }) => {
  const fontSize = {
    xs: "12px",
    sm: "12px",
    md: "16px",
    lg: "1rem",
  };
  return (
    <Grid item xs={12} sm={gridSize}>
      <Typography
        sx={{
          display: { xs: "block", sm: "none" },
          fontSize: fontSize,
          textTransform: "capitalize",
        }}
      >
        <b>{label}:</b> {value}
      </Typography>
      <Typography
        noWrap={noWrap}
        sx={{
          display: { xs: "none", sm: "block" },
          fontSize: fontSize,
        }}
      >
        {value}
      </Typography>
    </Grid>
  );
};

const ExternalExamCard = ({
  row,
  patientId,
  isSelected,
  headCells,
  handleCheckboxClick,
  handleViewReportClick,
}) => {
  const { t } = useTranslation();
  const { clinicTimezone } = useClinic();

  const eyeTranslation = {
    Left: t("word_left"),
    Right: t("word_right"),
    Binocular: t("word_binocular"),
  };

  const completionDate = useMemo(
    () =>
      getLocalDate(
        row?.completionDate ??
          DateTime.now().setZone(clinicTimezone).toUTC().toISO(),
        clinicTimezone
      ),
    [row, clinicTimezone]
  );

  return (
    <Grid
      container
      py={0}
      sx={{ display: "flex", alignItems: "center", flexWrap: "no-wrap" }}
    >
      <Grid item xs={12}>
        <Card
          elevation={0}
          key={row?.id}
          sx={{
            mb: "16px",
            py: "8px",
            borderRadius: "8px",
            backgroundColor: isSelected ? "#E2772E" : "#202338",
            color: "#FFF",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
            height: { xs: 120, sm: 52 },
            display: "flex",
            jutifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            container
            py={0}
            sx={{
              display: { xs: "grid", sm: "flex" },
              gridTemplateColumns: { xs: "13% 57% 30%", sm: "none" },
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <Grid item sm={1} sx={{ ml: { xs: 0, sm: 0 } }}>
              <CustomCheckbox
                checkedFill="#E2772E"
                checked={isSelected}
                onClick={() => handleCheckboxClick(row.id)}
              />
            </Grid>
            <Grid
              item
              sm={10}
              display={{ sx: "block", sm: "flex" }}
              flexDirection={{ sm: "space-between" }}
              alignItems="center"
            >
              <InfoDisplay
                label={headCells[1].label}
                value={eyeTranslation[row?.eye]}
                gridSize={headCells[1].gridSize}
              />
              <InfoDisplay
                label={headCells[2].label}
                value={`${row?.algorithmTypeReadable} | ${gridLabels[row?.gridType]}`}
                gridSize={headCells[2].gridSize}
              />
              <InfoDisplay
                label={headCells[3].label}
                value={completionDate}
                gridSize={headCells[3].gridSize}
              />
            </Grid>
            <Grid item sm={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "end" },
                  mr: { xs: 0, sm: 1 },
                }}
              >
                <Tooltip title={t("word_view_results")}>
                  <span>
                    <IconButton
                      data-testid={`view-results-${row?.id}`}
                      sx={{
                        height: "40px",
                        color: "inherit",
                      }}
                      onClick={() => handleViewReportClick(patientId, row?.id)}
                    >
                      <ViewFileIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ExternalExamCard;
